import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  total: number;
}

export interface PaginationResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

@Injectable()
export class AutocompleteService<T> {
  private itemsList: T[] = [];
  private fetchFn!: (params: any) => Observable<PaginationResponse<T>>;

  currentPage = 1;
  lastPage = 1;
  total = 0;
  perPage = 10;
  search = '';

  loading = false;
  loadingMore = false;

  items$ = new BehaviorSubject<T[]>([]);

  init(
    fetchFn: (params: any) => Observable<PaginationResponse<T>>,
    perPage = 10,
  ) {
    this.fetchFn = fetchFn;
    this.perPage = perPage;
  }

  loadFirstPage(search: string = '', prependItems: T[] = []) {
    if (!this.fetchFn) throw new Error('PaginationService not initialized');

    this.loading = true;
    this.search = search;
    this.currentPage = 1;

    const params: any = { per_page: this.perPage, page: 1 };
    if (search) {
      params['filters[name]'] = search;
      params['filters[title]'] = search;
      params['filters[search_input]'] = search;
    }

    this.fetchFn(params)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((res) => {
        this.itemsList = res.data;
        this.currentPage = res.meta.current_page;
        this.lastPage = res.meta.last_page;
        this.total = res.meta.total;

        prependItems.forEach((item: any) => {
          if (!(this.itemsList as any[]).some((i) => i.id === item.id)) {
            this.itemsList.unshift(item);
          }
        });

        this.items$.next(this.itemsList);
      });
  }

  loadNextPage() {
    if (!this.fetchFn || this.currentPage >= this.lastPage || this.loadingMore)
      return;

    this.loadingMore = true;
    const nextPage = this.currentPage + 1;

    const params: any = { per_page: this.perPage, page: nextPage };
    if (this.search) params['filters[name]'] = this.search;

    this.fetchFn(params)
      .pipe(finalize(() => (this.loadingMore = false)))
      .subscribe((res) => {
        const newItems = res.data.filter(
          (item: any) =>
            !(this.itemsList as any[]).some((i) => i.id === item.id),
        );

        this.itemsList = [...this.itemsList, ...newItems];
        this.currentPage = res.meta.current_page;
        this.lastPage = res.meta.last_page;
        this.total = res.meta.total;

        this.items$.next(this.itemsList);
        let panel = document.body.querySelector('.mdc-menu-surface--open');
        if (panel) {
          panel.scrollTop = panel.scrollTop - 50;
        }
      });
  }

  reset() {
    this.itemsList = [];
    this.currentPage = 1;
    this.lastPage = 1;
    this.total = 0;
    this.search = '';
    this.items$.next([]);
  }
}
